import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, REACTIONS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { EmojiButton } from "./EmojiFeeling";
import { LinkPreview } from "./LinkPreview";
import { GifPicker } from "./GifPicker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Send, CornerDownRight, Pencil, Trash2, Ban, AtSign, PawPrint, Flame, Heart, Star, Frown, ThumbsDown, SmilePlus } from "lucide-react";
import { toast } from "sonner";

const RICONS = { PawPrint, Flame, Heart, Star, Frown, ThumbsDown };
const RByType = Object.fromEntries(REACTIONS.map((r) => [r.type, r]));
function ReactionIcon({ type, className }) {
  const r = RByType[type]; if (!r) return null;
  const Icon = RICONS[r.icon] || PawPrint;
  return <Icon className={className} style={{ color: r.color }} />;
}

function CommentItem({ c, depth, postAuthorId, onReply, onEdit, onDelete, onBlock }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.text);
  const [reactions, setReactions] = useState(c.reactions || { counts: {}, total: 0, mine: null, reactions: [] });
  const [rOpen, setROpen] = useState(false);
  const mine = c.author.id === user.id;
  const canBlock = user.id === postAuthorId && c.author.id !== user.id;
  const orderedTypes = REACTIONS.map((r) => r.type).filter((t) => reactions.counts?.[t]);
  const react = async (type) => {
    setROpen(false);
    try { const { data } = await api.post(`/comments/${c.id}/react`, { type }); setReactions(data.reactions); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div style={{ marginLeft: depth * 24 }} className="mb-2" data-testid={`comment-${c.id}`}>
      <div className="flex gap-2">
        <button onClick={() => navigate(`/profile/${c.author.id}`)}><Avatar user={c.author} size="sm" showPresence /></button>
        <div className="flex-1">
          <div className="bg-spotter-cream border-2 border-black rounded-2xl px-3 py-2">
            <div className="text-sm font-bold">{c.author.name} {c.edited && <span className="text-[10px] font-normal text-spotter-maroon">(edited)</span>}</div>
            {editing ? (
              <div className="flex gap-2 mt-1">
                <Input value={text} onChange={(e) => setText(e.target.value)} className="border-2 border-black h-8" data-testid={`comment-edit-input-${c.id}`} />
                <Button size="sm" data-testid={`comment-edit-save-${c.id}`} onClick={() => { onEdit(c.id, text); setEditing(false); }} className="bg-spotter-maroon text-spotter-cream h-8">Save</Button>
              </div>
            ) : (
              <>
                <div className="text-sm whitespace-pre-wrap">{c.text}</div>
                {c.gif_url && <img src={c.gif_url} alt="GIF" data-testid={`comment-gif-img-${c.id}`} className="mt-1 rounded-lg border-2 border-black max-h-56 w-auto" />}
                <LinkPreview text={c.text} className="max-w-sm" />
              </>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-spotter-maroon flex-wrap">
            <span>{new Date(c.created_at).toLocaleString()}</span>
            <Popover open={rOpen} onOpenChange={setROpen}>
              <PopoverTrigger asChild>
                <button data-testid={`comment-react-trigger-${c.id}`} className="font-semibold hover:underline flex items-center gap-1">
                  {reactions.mine ? <><ReactionIcon type={reactions.mine} className="w-3.5 h-3.5" />{RByType[reactions.mine]?.label}</> : <><SmilePlus className="w-3 h-3" />React</>}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-1.5 border-2 border-black bg-white flex gap-1" side="top">
                {REACTIONS.map((r) => (
                  <button key={r.type} data-testid={`comment-react-${r.type}-${c.id}`} title={r.label} onClick={() => react(r.type)} className="p-1 hover:scale-125 transition-transform">
                    <ReactionIcon type={r.type} className="w-5 h-5" />
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            {orderedTypes.length > 0 && (
              <span className="inline-flex items-center gap-1" data-testid={`comment-reactions-${c.id}`}>
                {orderedTypes.map((t) => <span key={t} className="inline-flex items-center gap-0.5"><ReactionIcon type={t} className="w-3.5 h-3.5" />{reactions.counts[t]}</span>)}
              </span>
            )}
            <button data-testid={`comment-reply-${c.id}`} onClick={() => onReply(c)} className="font-semibold hover:underline flex items-center gap-1"><CornerDownRight className="w-3 h-3" />Reply</button>
            {mine && <button data-testid={`comment-edit-${c.id}`} onClick={() => setEditing(true)} className="font-semibold hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>}
            {mine && <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c.id)} className="font-semibold hover:underline text-red-700 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>}
            {canBlock && <button data-testid={`comment-block-${c.id}`} onClick={() => onBlock(c.author)} className="font-semibold hover:underline text-red-700 flex items-center gap-1"><Ban className="w-3 h-3" />Block</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Comments({ postId, postAuthorId, onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [tagged, setTagged] = useState([]); // [{id, username, name}]
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [tagOpen, setTagOpen] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/posts/${postId}/comments`);
    setComments(data);
    onCountChange?.(data.length);
  };
  useEffect(() => { load(); }, [postId]);

  useEffect(() => {
    if (!tagOpen) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data.slice(0, 8)); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery, tagOpen]);

  const addTag = (u) => {
    if (!tagged.some((t) => t.id === u.id)) {
      setTagged((prev) => [...prev, u]);
      setText((prev) => `${prev}${prev && !prev.endsWith(" ") ? " " : ""}@${u.username} `);
    }
    setTagQuery(""); setTagOpen(false);
  };

  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/posts/${postId}/comments`, { text, parent_id: replyTo?.id || null, tagged_user_ids: tagged.map((t) => t.id) });
      setText(""); setReplyTo(null); setTagged([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const sendGif = async (url) => {
    try {
      await api.post(`/posts/${postId}/comments`, { gif_url: url, parent_id: replyTo?.id || null, tagged_user_ids: tagged.map((t) => t.id) });
      setReplyTo(null); setTagged([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const edit = async (id, newText) => {
    try { await api.put(`/comments/${id}`, { text: newText }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/comments/${id}`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const block = async (author) => {
    try { await api.post(`/posts/${postId}/block-commenter`, { to_user_id: author.id }); toast.success(`${author.name} can no longer comment here`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const top = comments.filter((c) => !c.parent_id);
  const repliesOf = (id) => comments.filter((c) => c.parent_id === id);
  const renderTree = (c, depth) => (
    <div key={c.id}>
      <CommentItem c={c} depth={depth} postAuthorId={postAuthorId} onReply={setReplyTo} onEdit={edit} onDelete={del} onBlock={block} />
      {repliesOf(c.id).map((r) => renderTree(r, Math.min(depth + 1, 4)))}
    </div>
  );

  return (
    <div className="bg-white border-t-2 border-black p-4" data-testid={`comments-${postId}`}>
      <div className={`flex ${user?.profile_frame ? "gap-5" : "gap-2"} mb-3`}>
        <Avatar user={user} size="sm" />
        <div className="flex-1">
          {replyTo && (
            <div className="text-xs text-spotter-maroon mb-1 flex items-center gap-1">Replying to <b>{replyTo.author.name}</b>
              <button onClick={() => setReplyTo(null)} className="underline">cancel</button></div>
          )}
          <div className="flex gap-2">
            <Input ref={inputRef} data-testid={`comment-input-${postId}`} value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a comment…" className="border-2 border-black" />
            <Button data-testid={`comment-submit-${postId}`} onClick={add} className="bg-spotter-maroon text-spotter-cream border-2 border-black"><Send className="w-4 h-4" /></Button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <EmojiButton testid={`comment-emoji-${postId}`} onPick={(e) => setText((prev) => prev + e)} />
            <GifPicker onSelect={sendGif}
              trigger={<button type="button" data-testid={`comment-gif-${postId}`} title="Add a GIF" className="text-xs font-heading font-bold text-spotter-maroon border-2 border-black rounded px-1.5 py-0.5 hover:bg-spotter-cream">GIF</button>} />
            <Popover open={tagOpen} onOpenChange={setTagOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid={`comment-tag-${postId}`} title="Tag someone"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 border-black text-xs font-heading font-bold bg-white hover:bg-spotter-cream">
                  <AtSign className="w-4 h-4 text-spotter-gold" />Tag
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 border-2 border-black bg-white p-2">
                <Input autoFocus data-testid={`comment-tag-input-${postId}`} value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} placeholder="Search people…" className="border-2 border-black h-8 mb-2" />
                <div className="max-h-48 overflow-y-auto space-y-0.5">
                  {tagResults.map((u) => (
                    <button key={u.id} type="button" data-testid={`comment-tag-result-${u.id}`} onClick={() => addTag(u)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-spotter-cream text-left">
                      <Avatar user={u} size="sm" /><span className="text-sm font-semibold truncate">{u.name} <span className="text-spotter-maroon/60">@{u.username}</span></span>
                    </button>
                  ))}
                  {tagQuery && tagResults.length === 0 && <div className="text-xs text-spotter-maroon px-2 py-1">No one found.</div>}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      {top.map((c) => renderTree(c, 0))}
      {top.length === 0 && <div className="text-center text-sm text-spotter-maroon py-2">No comments yet.</div>}
    </div>
  );
}
